/**
 * Address autofill utility using Google Places API
 * Provides Google-style address lookup to autofill city, state, zip
 */

export interface AddressComponents {
  city: string;
  state: string;
  zip: string;
  fullAddress?: string;
}

export interface AutofillResult {
  success: boolean;
  data?: AddressComponents;
  error?: string;
}

/**
 * Autofill address using Google Places API
 * @param address - The address string to search for
 * @returns Promise with autofilled address components
 */
export async function autofillAddress(address: string): Promise<AutofillResult> {
  if (!address || address.trim().length < 5) {
    return {
      success: false,
      error: 'Address must be at least 5 characters',
    };
  }

  // Check if Google Places API key is configured
  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  
  if (!apiKey) {
    // If no API key, return error but don't block the user
    console.warn('Google Places API key not configured. Address autofill will not work.');
    return {
      success: false,
      error: 'Address autofill not configured',
    };
  }

  try {
    // Use Google Places Autocomplete API
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(address)}&key=${apiKey}&types=address`
    );

    if (!response.ok) {
      throw new Error(`Google Places API error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.status === 'OK' && data.predictions && data.predictions.length > 0) {
      // Get the first prediction's place details
      const placeId = data.predictions[0].place_id;
      
      // Fetch place details to get address components
      const detailsResponse = await fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=address_components,formatted_address&key=${apiKey}`
      );

      if (!detailsResponse.ok) {
        throw new Error(`Google Places Details API error: ${detailsResponse.statusText}`);
      }

      const detailsData = await detailsResponse.json();

      if (detailsData.status === 'OK' && detailsData.result) {
        const components = parseAddressComponents(detailsData.result.address_components);
        return {
          success: true,
          data: {
            ...components,
            fullAddress: detailsData.result.formatted_address,
          },
        };
      }
    }

    return {
      success: false,
      error: 'No address found',
    };
  } catch (error) {
    console.error('Error in address autofill:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Parse Google Places API address components into our format
 */
function parseAddressComponents(components: any[]): AddressComponents {
  const result: AddressComponents = {
    city: '',
    state: '',
    zip: '',
  };

  for (const component of components) {
    const types = component.types;

    if (types.includes('locality') || types.includes('sublocality')) {
      result.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name; // Use state abbreviation
    } else if (types.includes('postal_code')) {
      result.zip = component.long_name;
    }
  }

  return result;
}

/**
 * Simple address validation (fallback when API is not available)
 * Attempts to extract city, state, zip from common address formats
 */
export function parseAddressManually(address: string): AddressComponents | null {
  // Common patterns:
  // "123 Main St, New York, NY 10001"
  // "123 Main St, New York NY 10001"
  // "New York, NY 10001"
  
  const patterns = [
    // City, State ZIP
    /([^,]+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/,
    // Address, City, State ZIP
    /.+,\s*([^,]+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/,
  ];

  for (const pattern of patterns) {
    const match = address.match(pattern);
    if (match) {
      return {
        city: match[1]?.trim() || '',
        state: match[2]?.trim() || '',
        zip: match[3]?.trim() || '',
      };
    }
  }

  return null;
}
